import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useAuth } from "../../app/AuthContext";
import { api, ApiError } from "../../shared/api/client";
import type {
  BookableReleaseView,
  ReleaseSlotBookingView,
  SlotApproverView,
} from "../../shared/types/api";
import {
  Button,
  Drawer,
  Field,
  InlineAlert,
  Select,
  StatusBadge,
  Textarea,
  cn,
} from "../../shared/ui";

/**
 * Asking for a place on a release day.
 *
 * <p>The day is not chosen here. A release goes out when it goes out, and the booking sits on
 * that day — so the picker offers the releases already planned for the day that was clicked, and
 * a release with a different date is simply not on the list. Choosing a day here would make
 * booking a second way to move a release date, beside the one that asks for a reason.
 */
export function BookSlotDrawer({
  open,
  date,
  morningLeft,
  afternoonLeft,
  onClose,
  onSaved,
}: {
  open: boolean;
  date: string | null;
  morningLeft: number;
  afternoonLeft: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  /* Defaults to whichever half still has room, so the common case is one click fewer. */
  const [slot, setSlot] = useState<"MORNING" | "AFTERNOON">("MORNING");
  const [releaseId, setReleaseId] = useState("");
  const [note, setNote] = useState("");
  const [emergency, setEmergency] = useState(false);
  const [emergencyReason, setEmergencyReason] = useState("");
  const [agreementNote, setAgreementNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSlot(morningLeft > 0 ? "MORNING" : "AFTERNOON");
      setReleaseId("");
      setNote("");
      setEmergency(false);
      setEmergencyReason("");
      setAgreementNote("");
      setError(null);
    }
  }, [open, morningLeft]);

  /*
   * Asked of the server rather than filtered here. Which day a release goes out depends on its
   * own time zone, which the browser does not have — comparing the UTC date of an instant gets
   * the right answer for a morning release and the wrong one for an evening release in
   * Asia/Kolkata, and gets it wrong silently by showing an empty list.
   */
  const releases = useQuery({
    queryKey: ["release-slots", "bookable", date],
    queryFn: () => api.get<BookableReleaseView[]>(`/api/v1/release-slots/bookable?date=${date}`),
    enabled: open && date != null,
  });

  const onThatDay = releases.data ?? [];

  /*
   * Who would be left without a place if this booking takes one. Asked of the server once a
   * release is chosen, because the answer depends on when each release came to claim the day —
   * which the browser has no way of knowing. Empty for almost every booking.
   */
  const contested = useQuery({
    queryKey: ["release-slots", "contention", releaseId],
    queryFn: () =>
      api.get<BookableReleaseView[]>(`/api/v1/releases/${releaseId}/slot-contention`),
    enabled: open && releaseId !== "",
  });

  /* An emergency takes a place over the day's limit, so it costs nobody theirs. */
  const ahead = emergency ? [] : (contested.data ?? []);
  const mustAgree = ahead.length > 0;

  const book = useMutation({
    mutationFn: () =>
      api.post("/api/v1/release-slots/bookings", {
        releaseId,
        slot,
        note: note.trim() === "" ? null : note.trim(),
        emergencyReason: emergency ? emergencyReason.trim() : null,
        agreementNote: mustAgree ? agreementNote.trim() : null,
      }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : "Could not book the slot."),
  });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Book a release slot"
      subtitle={
        date
          ? `${formatDay(date)} — the project manager, or whoever they named, decides it.`
          : undefined
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => book.mutate()}
            disabled={
              releaseId === ""
              || (emergency && emergencyReason.trim() === "")
              || (mustAgree && agreementNote.trim() === "")
            }
            loading={book.isPending}
          >
            Ask for the slot
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <Field label="Half-day" required>
          <div className="grid grid-cols-2 gap-2">
            <SlotChoice
              label="Morning"
              left={morningLeft}
              chosen={slot === "MORNING"}
              onChoose={() => setSlot("MORNING")}
            />
            <SlotChoice
              label="Afternoon"
              left={afternoonLeft}
              chosen={slot === "AFTERNOON"}
              onChoose={() => setSlot("AFTERNOON")}
            />
          </div>
        </Field>

        <Field
          label="Release"
          required
          hint="Only releases already planned for this day. A release moves day through a date change, not through a booking."
        >
          <Select value={releaseId} onChange={(event) => setReleaseId(event.target.value)}>
            <option value="">Choose a release</option>
            {onThatDay.map((release) => (
              <option key={release.id} value={release.id}>
                {release.projectName} — {release.releaseName}
                {release.version ? ` (${release.version})` : ""}
              </option>
            ))}
          </Select>
        </Field>

        {/*
          * The conversation this screen exists to start. Shown once a release is chosen and only
          * when the place actually runs out, because a warning that appears when there is room
          * for everybody is one people learn to scroll past.
          */}
        {mustAgree && (
          <InlineAlert tone="warning">
            <span className="font-medium">
              {ahead.map((release) => release.projectName).join(", ")}{" "}
              {ahead.length === 1 ? "also goes" : "also go"} out on {formatDay(date ?? "")}.
            </span>{" "}
            They planned that day before you did and have not booked yet — taking this place
            leaves them none.
            <ul className="mt-2 space-y-0.5">
              {ahead.map((release) => (
                <li key={release.id} className="text-xs">
                  {release.projectName} — {release.releaseName}
                  {release.version ? ` (${release.version})` : ""}
                </li>
              ))}
            </ul>
            <span className="mt-2 block">
              Talk to them first. If they are happy for you to go ahead, say so below and the
              booking continues.
            </span>
          </InlineAlert>
        )}

        {mustAgree && (
          <Field
            label="What you agreed"
            required
            hint="Whoever approves this sees it. Name who you spoke to and what was settled."
          >
            <Textarea
              rows={2}
              value={agreementNote}
              placeholder="Spoke to Meera — Accounting goes out in the afternoon instead."
              onChange={(event) => setAgreementNote(event.target.value)}
            />
          </Field>
        )}

        {releases.isSuccess && onThatDay.length === 0 && (
          <InlineAlert tone="info">
            Nothing is planned to go out on {formatDay(date ?? "")}. Set a release's date to that
            day first, and it will appear here.
          </InlineAlert>
        )}

        <Field label="Note" hint="Optional. Anything whoever decides this should know.">
          <Textarea rows={2} value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>

        {/* Over the limit rather than around it: still approved, and marked as what it is. */}
        <label className="flex items-start gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            checked={emergency}
            onChange={(event) => setEmergency(event.target.checked)}
            className="mt-0.5"
          />
          <span>
            This is an emergency and cannot wait for a free slot
            <span className="block text-xs text-ink-500">
              It takes a place over the day's limit, still needs the same approval, and is shown
              on the calendar as an exception.
            </span>
          </span>
        </label>

        {emergency && (
          <Field
            label="Why it cannot wait"
            required
            hint="Whoever approves this over the day's limit has only what you write here to go on."
          >
            <Textarea
              rows={3}
              value={emergencyReason}
              placeholder="Payments are failing in production for all customers."
              onChange={(event) => setEmergencyReason(event.target.value)}
            />
          </Field>
        )}
      </div>
    </Drawer>
  );
}

/**
 * One of the day's two halves, with the room left in it.
 *
 * <p>The count is what makes this a choice rather than a coin toss: a half-day with no places
 * left can still be booked, but only as an emergency, and somebody should see that before they
 * pick it rather than after they are refused.
 */
function SlotChoice({
  label,
  left,
  chosen,
  onChoose,
}: {
  label: string;
  left: number;
  chosen: boolean;
  onChoose: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onChoose}
      className={cn(
        "rounded-lg border px-3 py-2 text-left transition-colors",
        chosen
          ? "border-brand-500 bg-brand-50 ring-1 ring-brand-400"
          : "border-ink-200 hover:border-ink-300 hover:bg-ink-50",
      )}
    >
      <span className="block text-sm font-medium text-ink-900">{label}</span>
      <span className={cn("block text-xs", left > 0 ? "text-ink-500" : "text-amber-700")}>
        {left > 0 ? `${left} free` : "Full — emergency only"}
      </span>
    </button>
  );
}

/** Approving or declining a booking, and giving a place back. */
export function SlotDecisionDrawer({
  booking,
  onClose,
  onSaved,
}: {
  booking: ReleaseSlotBookingView | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    setNote("");
    setError(null);
  }, [booking]);

  /*
   * Who decides this booking: the project's named approvers, or its manager when none are
   * named. An administrator may decide it regardless — so this is fetched to tell them when
   * they are about to, rather than to stop them.
   */
  const approvers = useQuery({
    queryKey: ["slot-approvers", booking?.projectId],
    queryFn: () =>
      api.get<SlotApproverView[]>(`/api/v1/release-slots/projects/${booking!.projectId}/slot-approvers`),
    enabled: booking != null,
  });

  const theirs = (approvers.data ?? []).some((a) => a.employeeId === user?.employeeId);
  const overriding = booking != null && !theirs && (approvers.data?.length ?? 0) > 0;

  const decide = useMutation({
    mutationFn: (approved: boolean) =>
      api.post(`/api/v1/release-slots/bookings/${booking?.id}/decide`, {
        approved,
        note: note.trim() === "" ? null : note.trim(),
      }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : "Could not record the decision."),
  });

  return (
    <Drawer
      open={booking != null}
      onClose={onClose}
      title="Decide this booking"
      subtitle={
        booking
          ? `${booking.projectName} — ${booking.releaseName} on ${formatDay(booking.slotDate)}`
          : undefined
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={() => decide.mutate(false)}
            loading={decide.isPending}
          >
            Decline
          </Button>
          <Button onClick={() => decide.mutate(true)} loading={decide.isPending}>
            Approve
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {booking?.emergency && (
          <InlineAlert tone="warning">
            <span className="font-medium">Over the day's limit.</span> This was booked as an
            emergency: “{booking.emergencyReason}”
          </InlineAlert>
        )}

        {/*
          * An administrator deciding somebody else's booking is allowed and recorded as an
          * override. Saying so beforehand is the point: a decision that reads as "the project
          * agreed" when it was signed off over their head is the thing this warning prevents.
          */}
        {overriding && (
          <InlineAlert tone="warning">
            <span className="font-medium">This is not yours to decide.</span>{" "}
            {approvers.data?.map((a) => a.fullName).join(", ")}{" "}
            {approvers.data?.length === 1 ? "decides" : "decide"} bookings on{" "}
            {booking?.projectName}. You may still approve it as an administrator — it will be
            recorded as an override rather than as the project having agreed.
          </InlineAlert>
        )}

        {/* The thing worth weighing: this place belonged to somebody who planned the day first,
            and the booking only exists because the two teams settled it between them. */}
        {booking?.agreementNote && (
          <InlineAlert tone="warning">
            <span className="font-medium">Another project planned this day first.</span> This
            booking took the last place in front of them. They were told, and this is what was
            agreed: “{booking.agreementNote}”
          </InlineAlert>
        )}

        {booking && (
          <>
            <Field label="Slot">
              <p className="text-sm text-ink-700">
                {booking.slot === "MORNING" ? "Morning" : "Afternoon"} of{" "}
                {formatDay(booking.slotDate)}
              </p>
            </Field>
            <Field label="Asked by">
              <p className="text-sm text-ink-700">
                {booking.requestedByName}{" "}
                <StatusBadge tone="amber">Waiting</StatusBadge>
              </p>
            </Field>
            {booking.note && (
              <Field label="Note">
                <p className="text-sm text-ink-700">“{booking.note}”</p>
              </Field>
            )}
          </>
        )}

        <Field
          label="Note"
          hint="Optional on approval. Worth writing on a refusal — it is all the team will see."
        >
          <Textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
      </div>
    </Drawer>
  );
}

function formatDay(iso: string): string {
  if (!iso) {
    return "";
  }
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}
